import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Stethoscope, Activity, Footprints, Scan, Microscope, HeartPulse, Sparkles, ArrowRight, ExternalLink as ExtLinkIcon } from "lucide-react";
import { dict } from "../i18n";
import { fetchContent } from "../lib/api";
import SEO from "../components/SEO";
import { getSectionByServiceId, pricingSectionUrl } from "../lib/pricingSections";
import { pagePath } from "../lib/routes";

const ICONS = { stethoscope: Stethoscope, activity: Activity, footprints: Footprints, scan: Scan, microscope: Microscope, "heart-pulse": HeartPulse, sparkles: Sparkles };

export default function Services({ lang }) {
  const t = dict[lang];
  const [clinic, setClinic] = useState(null);

  useEffect(() => {
    fetchContent().then(setClinic).catch(() => {});
  }, []);

  return (
    <>
      <SEO lang={lang} page="services" />
      <section className="container-page pt-12 pb-8" data-testid="services-page">
        <div className="chip mb-3">{t.sections.services.tag}</div>
        <h1 className="section-title">{t.sections.services.title}</h1>
        <p className="text-surface-inkSoft mt-3 text-[15px] max-w-2xl">{t.hero.subtitle}</p>
      </section>

      <section className="container-page pb-16">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {(clinic?.services || []).map((s) => {
            const Icon = ICONS[s.icon] || Stethoscope;
            const isExternal = !!s.externalUrl;
            const section = getSectionByServiceId(s.id);
            const pricingHref = section
              ? pricingSectionUrl(section.slugs.et, lang)
              : pagePath("pricing", lang);
            return (
              <div
                key={s.id}
                className={`card-clinic ${isExternal ? "border-teal-300/60 bg-gradient-to-br from-white to-teal-200/10" : ""}`}
                data-testid={`service-detail-${s.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`h-14 w-14 rounded-xl flex items-center justify-center shrink-0 ${isExternal ? "bg-sapphire-700 text-white" : "bg-teal-200/50 text-teal-500"}`}>
                    <Icon className="h-7 w-7" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-surface-ink mb-2">{s.title[lang]}</h2>
                    <p className="text-[14px] text-surface-inkSoft leading-relaxed">{s.summary[lang]}</p>
                    {isExternal ? (
                      <a
                        href={s.externalUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`service-external-link-${s.id}`}
                        className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-medium text-sapphire-700 hover:gap-2.5 transition-all"
                      >
                        {lang === "et" ? "Ava AI Kliinik" : lang === "ru" ? "Открыть AI Клинику" : "Open AI Clinic"}
                        <ExtLinkIcon className="h-3.5 w-3.5" />
                      </a>
                    ) : (
                      <Link
                        to={pricingHref}
                        data-testid={`service-pricing-link-${s.id}`}
                        className="mt-4 inline-flex items-center gap-1.5 text-[14px] font-medium text-sapphire-700 hover:gap-2.5 transition-all"
                      >
                        {t.nav.pricing} <ArrowRight className="h-3.5 w-3.5" />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}
